import { ArrowRight, PlayCircle } from "lucide-react";

const trustSignals = [
  { label: "Live store data", detail: "Ratings, reviews, app metadata where available" },
  { label: "Estimated scores", detail: "Equinox scoring logic and growth potential" },
  { label: "Benchmark-based insights", detail: "Competitor and recommendation layer" },
];

export const AsoHeroForm = ({ form, onChange, onSubmit, onWatchSample, submitting }) => {
  return (
    <section className="px-6 pb-12 pt-16 sm:px-8 lg:px-12 lg:pb-16 lg:pt-20" data-testid="aso-engine-hero-section">
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-end">
        <div>
          <p className="section-kicker">Equinox ASO AI Engine</p>
          <h1 className="section-heading mt-5 max-w-[11ch] text-white">Discover Your App’s Growth Potential in 60 Seconds</h1>
          <p className="section-copy mt-6 text-white/[0.64]">
            Get an AI-powered ASO audit, competitor analysis, and growth recommendations instantly — inside a premium founder-grade experience.
          </p>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-white/[0.52]" data-testid="aso-engine-trust-copy">
            Built from <span className="text-white">live store data where available</span>, then layered with <span className="text-white">Equinox estimated scoring</span> and <span className="text-white">benchmark-based insight</span>.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm text-white/[0.62]" data-testid="aso-engine-proof-pills">
            {[
              "Play Store + App Store",
              "AI video-style briefing",
              "ASO score + competitor gap",
            ].map((item) => (
              <span key={item} className="rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-2 uppercase tracking-[0.18em]">
                {item}
              </span>
            ))}
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3" data-testid="aso-engine-trust-legend">
            {trustSignals.map((item) => (
              <div key={item.label} className="rounded-[1.4rem] border border-white/[0.08] bg-white/[0.03] p-4">
                <p className="text-[11px] uppercase tracking-[0.2em] text-blue-100">{item.label}</p>
                <p className="mt-2 text-sm leading-6 text-white/[0.62]">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={onSubmit} className="editorial-shell premium-border glass-panel rounded-[2.3rem] p-6 lg:p-8" data-testid="aso-engine-hero-form">
          <div className="grid gap-4 md:grid-cols-2">
            {[
              { key: "founder_name", label: "Founder Name", type: "text", testId: "aso-founder-name-input" },
              { key: "work_email", label: "Work Email", type: "email", testId: "aso-work-email-input" },
              { key: "company_name", label: "Company Name", type: "text", testId: "aso-company-name-input" },
              { key: "app_url", label: "Google Play / App Store URL", type: "url", testId: "aso-app-url-input", span: "md:col-span-2" },
            ].map((field) => (
              <label key={field.key} className={`block text-sm text-white/[0.76] ${field.span || ""}`}>
                <span>{field.label}</span>
                <input
                  type={field.type}
                  value={form[field.key]}
                  onChange={(event) => onChange(field.key, event.target.value)}
                  className="mt-3 h-12 w-full rounded-2xl border border-white/[0.08] bg-black/20 px-4 text-white outline-none transition-colors focus:border-blue-300/40"
                  data-testid={field.testId}
                  placeholder={field.key === "app_url" ? "https://play.google.com/store/apps/details?id=..." : ""}
                />
              </label>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="submit"
              disabled={submitting}
              className="glass-button glass-panel rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-all duration-300 hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="aso-generate-report-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                {submitting ? "Launching audit..." : "Generate AI ASO Report"}
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>

            <button
              type="button"
              onClick={onWatchSample}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-7 py-4 text-sm font-medium text-white/82 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-300/30 hover:bg-blue-400/10 hover:text-white"
              data-testid="aso-watch-sample-button"
            >
              <PlayCircle className="h-4 w-4" />
              Watch Sample Report
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};